/**
 * The application shell.
 *
 * It composes: providers → chrome → router. It contains NO screen. Chapter screens
 * arrive through `src/features/<slug>/manifest.ts` and are mounted by the router, which
 * is why an agent adding a chapter never edits this file.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMemo } from 'react';
import { registerQueryClient, shouldRetryRead } from '../api';
import { AppChrome } from './chrome/AppChrome';
import { FeedbackProvider } from './feedback/FeedbackProvider';
import { OptimaProvider } from './optima/OptimaProvider';
import { ScreenErrorBoundary } from './feedback/ScreenErrorBoundary';
import { AuthCallback } from './routes/AuthCallback';
import { LoginGate } from './routes/LoginGate';
import { Router, useUrlState } from './routes/router';
import { RunWordsAboutProvider } from '../planning';
import { ToastProvider, TooltipProvider } from '../primitives';
// G7.5 — stated in one place, read by the chrome and by the sign-in screen alike.
import { APP_VERSION, COMMIT_SHA } from '../lib/build-info';

/**
 * Keycloak's redirect URI for this app (src/auth/session.ts). That screen exchanges the
 * authorization code for a session and is intentionally chrome-less and UNGATED — an
 * unauthenticated visitor bouncing back from Keycloak must be able to reach it, or
 * `LoginGate` below would refuse the very screen that signs them in.
 */
const AUTH_CALLBACK_PATH = '/auth/callback';


function createQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        // G2 §2.1 budgets a warm first meaningful paint at 1.5 s. A stale-while-revalidate
        // window means a return to a screen paints from cache and refreshes behind it.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        // Two retries for a network failure or a 5xx; none for a 4xx the service answered
        // (a designed «not published» 404 is the same answer every time).
        retry: shouldRetryRead,
        refetchOnWindowFocus: false,
      },
      mutations: {
        // G2 §2.6 — "Optimistic UI with explicit failure recovery; NEVER a silent discard
        // of a planner's edit." Retries are off on mutations so a failure surfaces once
        // and is recoverable, rather than being retried into an ambiguous state.
        retry: 0,
      },
    },
  });
  // The reads a click makes outside React (an export's report walk) share this cache.
  registerQueryClient(client);
  return client;
}

export function App(): React.JSX.Element {
  const queryClient = useMemo(createQueryClient, []);
  const { url, navigate, replaceParams } = useUrlState();


  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ToastProvider>
          <RunWordsAboutProvider>
            {window.location.pathname === AUTH_CALLBACK_PATH ? (
              <AuthCallback />
            ) : (
              <LoginGate>
                <FeedbackProvider currentPath={url.path} params={url.params}>
                  <OptimaProvider currentPath={url.path} params={url.params} onNavigate={navigate}>
                    <AppChrome
                      currentPath={url.path}
                      params={url.params}
                      onNavigate={navigate}
                      version={APP_VERSION}
                      commit={COMMIT_SHA}
                    >
                      {/* Inside the chrome, so a screen that throws leaves the rail and the account menu standing. */}
                      <ScreenErrorBoundary resetKey={url.path}>
                        <Router url={url} onNavigate={navigate} onReplaceParams={replaceParams} />
                      </ScreenErrorBoundary>
                    </AppChrome>
                  </OptimaProvider>
                </FeedbackProvider>
              </LoginGate>
            )}
          </RunWordsAboutProvider>
        </ToastProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
